import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { FaceSmileIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaHandshake } from 'react-icons/fa';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import DealModal from '../../pages/deals/createDeal/dealModal';
import { formatFileSize, getFileIcon, validateAndFilterFiles } from '../../utils/utilMethods';
import { VIDEO_UPLOAD_ENABLED } from '../../config/featureFlags';

interface ChatInputProps {
    message: string;
    setMessage: React.Dispatch<React.SetStateAction<string>>;
    selectedFiles: File[];
    sending: boolean;
    uploadingFiles: boolean;
    handleSendMessage: () => void;
    handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
    handleFileSelect: (files: File[]) => void;
    removeFile: (index: number) => void;
    clearFileSelection: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    showEmojiPicker: boolean;
    setShowEmojiPicker: React.Dispatch<React.SetStateAction<boolean>>;
    inputAreaRef: React.RefObject<HTMLDivElement | any>;
    MAX_CHAR_LIMIT: number;
    getCurrentUserRole: () => string;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    selectedUser: any;
    selectedGroup: any;
    selectedChatType: string;
    selectedDealForView: any;
    setSelectedDealForView: React.Dispatch<React.SetStateAction<any>>;
    fetchMessages: (email: string, type: "INDIVIDUAL" | "GROUP") => Promise<void>;
    fetchGroupMessages: (id: number) => Promise<void>;
}

interface ImagePreviewProps {
    file: File;
    className: string;
}

// Shows a local preview of an image file and frees its object URL when done.
const ImagePreview: React.FC<ImagePreviewProps> = ({ file, className }) => {
    const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);

    useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

    return <img src={previewUrl} alt={file.name} className={className} />;
};

export const ChatInput: React.FC<ChatInputProps> = ({
    message,
    setMessage,
    selectedFiles,
    sending,
    uploadingFiles,
    handleSendMessage,
    handleKeyPress,
    handleFileSelect,
    removeFile,
    clearFileSelection,
    fileInputRef,
    showEmojiPicker,
    setShowEmojiPicker,
    inputAreaRef,
    MAX_CHAR_LIMIT,
    getCurrentUserRole,
    showModal,
    setShowModal,
    selectedUser,
    selectedGroup,
    selectedChatType,
    selectedDealForView,
    setSelectedDealForView,
    fetchMessages,
    fetchGroupMessages,
}) => {
    const { t } = useTranslation('chat');
    const isDesktop = useIsDesktop();
    const selectedFighterId = selectedUser?.profileDto?.id || undefined;

    const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>) => {
        const value = e.target.value;
        if (value.length <= MAX_CHAR_LIMIT) {
            setMessage(value);
        } else {
            setMessage(value.substring(0, MAX_CHAR_LIMIT));
        }
    };

    const isEditMode = !!selectedDealForView;

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement | any>) => {
        const pastedText = e.clipboardData.getData('text');
        const currentText = message;
        const availableSpace = MAX_CHAR_LIMIT - currentText.length;
        if (pastedText.length > availableSpace) {
            e.preventDefault();
            const truncatedText = pastedText.substring(0, availableSpace);
            setMessage(currentText + truncatedText);
        }
    };

    const onEmojiClick = (emoji: any) => {
        const emojiChar = emoji.native;
        if (message.length + emojiChar.length <= MAX_CHAR_LIMIT) {
            setMessage((prev) => prev + emojiChar);
        }
    };

    const isVideoFile = (file: File) => file.type.startsWith('video/');

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const allowedFiles = VIDEO_UPLOAD_ENABLED
            ? files
            : files.filter((file) => !isVideoFile(file));

        if (allowedFiles.length > 0) {
            const validFiles = validateAndFilterFiles(allowedFiles);
            handleFileSelect(validFiles);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const emojiPicker = (
        <div className="absolute bottom-full left-0 mb-3 z-50 shadow-2xl rounded-2xl overflow-hidden">
            <Picker
                data={data}
                onEmojiSelect={onEmojiClick}
                theme="light"
                set="native"
                showPreview={false}
                showSkinTones={false}
                emojiSize={20}
                perLine={8}
                maxFrequentRows={2}
            />
        </div>
    );

    const renderFilePreview = (file: File, index: number) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
            return (
                <div className="relative inline-block">
                    <ImagePreview
                        file={file}
                        className="w-16 h-16 object-cover rounded-xl border border-gray-200"
                    />
                    <button
                        onClick={() => removeFile(index)}
                        className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5 shadow"
                        disabled={uploadingFiles}
                    >
                        <XMarkIcon className="w-3 h-3" />
                    </button>
                </div>
            );
        } else {
            return (
                <div className="relative inline-flex items-center gap-2 bg-gray-100 rounded-xl p-2 border border-gray-200">
                    <div className="text-gray-600">{getFileIcon(file.name)}</div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate max-w-[120px]">{file.name}</p>
                        <p className="text-[10px] text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                        onClick={() => removeFile(index)}
                        className="text-gray-500 hover:text-gray-700 p-0.5"
                        disabled={uploadingFiles}
                    >
                        <XMarkIcon className="w-3 h-3" />
                    </button>
                </div>
            );
        }
    };

    const renderSelectedFiles = () => {
        if (selectedFiles.length === 0) return null;
        return (
            <div className="flex flex-wrap gap-2 max-w-full overflow-hidden p-3 bg-blue-50/60 border border-blue-100 rounded-2xl mb-2">
                <div className="w-full flex items-center justify-between mb-1">
                    <h4 className="text-xs font-medium text-blue-800">{t('filesToSend', 'Files to send')}</h4>
                    <button
                        onClick={clearFileSelection}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                        {t('clearAll', 'Clear all')}
                    </button>
                </div>
                {selectedFiles.map((file, index) => (
                    <div key={index}>{renderFilePreview(file, index)}</div>
                ))}
                {uploadingFiles && (
                    <div className="w-full flex items-center gap-2 text-xs text-blue-600 mt-1">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-400"></div>
                        <span>{t('uploading', 'Uploading...')}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            ref={inputAreaRef}
            className="self-stretch p-3 md:p-6 bg-white border-t border-gray-100 shrink-0"
        >
            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
                accept={
                    VIDEO_UPLOAD_ENABLED
                        ? "*/*"
                        : "image/*,.pdf,.doc,.docx,.txt,.zip,.rar"
                }
            />

            {renderSelectedFiles()}

            <div className="hidden lg:flex items-center gap-3 w-full">
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        className="w-9 h-9 min-w-[36px] bg-gray-100 hover:bg-gray-200/80 rounded-full flex items-center justify-center text-gray-700 transition-colors"
                        onClick={openFileDialog}
                        disabled={sending || uploadingFiles}
                        title={t('attachFile', 'Attach file')}
                    >
                        <PaperClipIcon className="w-4 h-4 text-gray-700" />
                    </button>

                    <div className="relative" data-emoji-picker>
                        <button
                            type="button"
                            className="w-9 h-9 min-w-[36px] bg-gray-100 hover:bg-gray-200/80 rounded-full flex items-center justify-center text-gray-700 transition-colors"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            title={t('emoji', 'Emoji')}
                        >
                            <FaceSmileIcon className="w-4 h-4 text-gray-700" />
                        </button>
                        {showEmojiPicker && isDesktop && emojiPicker}
                    </div>

                    {getCurrentUserRole() === "MANAGER" && (
                        <div>
                            <button
                                type="button"
                                className="h-9 px-3.5 bg-green-100 hover:bg-green-200/80 text-green-700 rounded-3xl flex items-center gap-1.5 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-colors whitespace-nowrap"
                                onClick={() => {
                                    setSelectedDealForView(null);
                                    setShowModal(true);
                                }}
                            >
                                <FaHandshake className="w-4 h-4 text-green-600" />
                                <span>{t("makeDeal", "Make a deal")}</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex items-center">
                    <input
                        type="text"
                        placeholder={t('messagePlaceholder', 'Type your message here ...')}
                        className="w-full h-9 px-3 py-2 bg-gray-100 hover:bg-gray-200/50 focus:bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 focus:outline-blue-500 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 text-gray-800 placeholder:text-gray-400 transition-all"
                        value={message}
                        onChange={handleMessageChange}
                        onPaste={handlePaste}
                        onKeyPress={handleKeyPress}
                        disabled={sending}
                    />
                </div>

                <button
                    type="button"
                    className="h-9 min-w-9 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-3xl flex justify-center items-center gap-1.5 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-colors shadow-sm shrink-0"
                    onClick={handleSendMessage}
                    disabled={(!message.trim() && selectedFiles.length === 0) || sending || uploadingFiles}
                >
                    {sending ? (
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                    ) : (
                        <span>{t('send', 'Send')}</span>
                    )}
                </button>
            </div>

            <div className="flex lg:hidden flex-col gap-2.5 w-full">
                <div className="flex items-center gap-2 w-full">
                    <input
                        type="text"
                        placeholder={t('messagePlaceholder', 'Type your message here ...')}
                        className="flex-1 h-10 px-3.5 py-2 bg-gray-100 focus:bg-white rounded-2xl outline-none border border-gray-200 focus:border-blue-500 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 text-gray-800 placeholder:text-gray-400 transition-all"
                        value={message}
                        onChange={handleMessageChange}
                        onPaste={handlePaste}
                        onKeyPress={handleKeyPress}
                        disabled={sending}
                    />
                    <button
                        type="button"
                        className="h-10 px-5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-2xl flex justify-center items-center gap-1.5 text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-5 transition-colors shadow-sm shrink-0"
                        onClick={handleSendMessage}
                        disabled={(!message.trim() && selectedFiles.length === 0) || sending || uploadingFiles}
                    >
                        {sending ? (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                        ) : (
                            <span>{t('send', 'Send')}</span>
                        )}
                    </button>
                </div>

                <div className="relative flex items-center gap-2">
                    <button
                        type="button"
                        className="w-9 h-9 min-w-[36px] bg-gray-100 hover:bg-gray-200/80 rounded-full flex items-center justify-center text-gray-700 transition-colors shrink-0"
                        onClick={openFileDialog}
                        disabled={sending || uploadingFiles}
                        title={t('attachFile', 'Attach file')}
                    >
                        <PaperClipIcon className="w-4 h-4 text-gray-700" />
                    </button>

                    <div data-emoji-picker>
                        <button
                            type="button"
                            className="w-9 h-9 min-w-[36px] bg-gray-100 hover:bg-gray-200/80 rounded-full flex items-center justify-center text-gray-700 transition-colors shrink-0"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            title={t('emoji', 'Emoji')}
                        >
                            <FaceSmileIcon className="w-4 h-4 text-gray-700" />
                        </button>
                        {showEmojiPicker && !isDesktop && emojiPicker}
                    </div>

                    {getCurrentUserRole() === "MANAGER" && (
                        <button
                            type="button"
                            className="h-9 px-3.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-2xl flex items-center gap-1.5 text-xs font-semibold font-['Plus_Jakarta_Sans'] leading-5 transition-colors whitespace-nowrap"
                            onClick={() => {
                                setSelectedDealForView(null);
                                setShowModal(true);
                            }}
                        >
                            <FaHandshake className="w-4 h-4 text-green-600" />
                            <span>{t("makeDeal", "Make a deal")}</span>
                        </button>
                    )}
                </div>
            </div>

            {showModal && (
                <DealModal
                    onClose={() => {
                        setShowModal(false);
                        setSelectedDealForView(null);
                    }}
                    opportunityCards={isEditMode ? [selectedDealForView] : []}
                    onEdit={async (card: any) => {
                        if (selectedChatType === 'INDIVIDUAL' && selectedUser) {
                            await fetchMessages(selectedUser.profileDto.id, 'INDIVIDUAL');
                        } else if (selectedChatType === 'GROUP' && selectedGroup) {
                            await fetchGroupMessages(selectedGroup.id);
                        }
                        setSelectedDealForView(null);
                    }}
                    onDelete={(cardId: any) => { }}
                    fighterId={selectedFighterId}
                    isGroupChat={!!selectedGroup}
                    groupMembers={selectedGroup?.members || []}
                    chatRoomId={selectedGroup?.id}
                    getUserRole={getCurrentUserRole}
                    editMode={!!selectedDealForView}
                    dealToEdit={selectedDealForView}
                />
            )}
        </div>
    );
};
